<template>
    <div>
        <rate
            v-model="form.rating"
            v-if="currentStep('rate')"
            :image="image"
            @input="form.errors.clear()"
            @submit="fromRate"
            :submitText="submitBtnRating"
            :question="ratingMessage"
            :form="form"
        />

        <feedback-review
            v-if="currentStep('feedback')"
            :image="image"
            :text="feedbackText"
            :form="form"
            :textSubmit="submitBtnFeedback"
            :textBack="backBtnFeedback"
            @submit="fromFeedback"
            @back="backFromFeedback"
        />

        <share
            v-if="currentStep('share')"
            :image="image"
            :sites="invite.review_sites"
            :question="shareMessage"
            :locationName="invite.location.name"
            :allowPrivateFeedback="campaign.allow_private_feedback"
            @feedback="askFeedbackFromShare"
            @back="backFromShare"
            @site="openSite"
        />

        <thank-you
            v-if="currentStep('thank_you')"
            :image="image"
            :text="thankYouMessage"
        />
    </div>
</template>

<script>
    import Form from '../../utils/Form';

    export default {
        props: {
            invite: { required: true, type: Object },
            review: { required: true, type: Object },
            image: { required: true, type: String },
            settings: { required: true, type: Object }
        },

        data() {
            return {
                form: this.setupForm(this.review),
                step: 'rate',
                feedback_text: 'Please tell us why you chose :rating stars',
                feedback_from_share: false,
                review_sites: ['google']
            };
        },

        mounted() {
            if (this.review.rating) {
                this.step = 'feedback';
            }
        },

        computed: {
            shareMessage() {
                return this.settings.share_message || 'Where would you like to leave your review?';
            },
            ratingMessage() {
                return this.settings.rating_message || 'How would you rate your experience with us?';
            },
            thankYouMessage() {
                return this.settings.thank_you_message || 'Thank you!';
            },
            backBtnRating() {
                return this.settings.submit_button_rating || 'Back';
            },
            submitBtnRating() {
                return this.settings.submit_button_rating || 'Submit';
            },
            submitBtnFeedback() {
                return this.settings.submit_button_feedback || 'Submit';
            },
            backBtnFeedback() {
                return this.settings.back_button_feedback || 'Back';
            },
            feedbackText() {
                return (
                    (this.settings.feedback_message && this.settings.feedback_message.replace(':rating', this.form.rating)) ||
                    this.feedback_text.replace(':rating', this.form.rating)
                );
            },
            campaign() {
                return this.invite.campaign;
            },
            positive_threshold() {
                return this.form.rating > this.campaign.threshold;
            },
            sharable() {
                return this.campaign.redirect_to_review_site && this.positive_threshold;
            },
            ask_for_feedback() {
                return this.campaign.ask_for_feedback;
            }
        },

        methods: {
            async save() {
                if (this.form.id) {
                    const { data } = await this.form.put(this.$laroute.route('api.reviews.update', { review: this.form.id }));
                    return (this.form = this.setupForm(data.data));
                }

                const { data } = await this.form.post(this.$laroute.route('api.reviews.store', { token: this.form.invite.token }));
                return (this.form = this.setupForm(data));
            },

            setupForm(review = {}) {
                return new Form({
                    ...review,
                    type: this.invite.campaign.type
                });
            },

            async fromRate() {
                try {
                    await this.save();
                    this.step = 'feedback';
                    if (this.sharable && !this.ask_for_feedback) {
                        this.step = 'share';
                    }
                } catch (error) {
                    this.onError(error);
                }
            },

            backFromFeedback() {
                this.step = 'rate';
            },

            backFromShare() {
                this.step = 'rate';
                if (!this.positive_threshold) {
                    this.step = 'feedback';
                }
            },

            fromFeedback() {
                this.save()
                    .then(() => {
                        this.step = 'thank_you';
                        if (this.sharable && !this.feedback_from_share) {
                            this.step = 'share';
                        }
                    })
                    .catch(this.onError.bind(this));
            },

            askFeedbackFromShare() {
                this.feedback_from_share = true;
                this.step = 'feedback';
                if (this.ask_for_feedback) {
                    this.step = 'thank_you';
                }
            },

            openSite(review_site) {
                window.location.href = review_site.url;
            },

            currentStep(step) {
                return this.step === step;
            },

            onError(error) {
                if (error.status !== 422) {
                    this.$alert({ type: 'error', message: error.data.message || 'Unknown error' });
                }
            }
        }
    };
</script>
